import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Dialog, DialogTitle, DialogContent, Button } from "@mui/material";
import { GridToolbar, DataGrid } from "@material-ui/data-grid";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FullScreenDialog from "../../components/Dialog/FullScreenDialog";
import PartnerClientDetails from "../../components/PartnerClientDetails/PartnerClientDetails";
import PartnerClientEditDetails from "../../components/PartnerClientDetails/PartnerClientEditDetails";
import { downloadKYCDocs } from "../../pages/Users/PartnersUsersDashboard";

export const ClientFilterDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    clients: any[];
    partners: any[];
    fetchUsers: () => void;
}> = ({ open, onClose, clients, partners, fetchUsers }) => {
    const toastr = require("toastr");
    const history = useHistory();
    const [search, setSearch] = useState("");
    const [filteredClients, setFilteredClients] = useState(clients);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedUserForView, setSelectedUserForView] = useState<any>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedUserForEdit, setSelectedUserForEdit] = useState<any>(null);
    const editDetailsRef = React.useRef<any>(null);

    useEffect(() => {
        const q = search.toLowerCase();
        const filtered = clients.filter((client) => {
            const fullName = `${client.first_name || ""} ${client.last_name || ""}`.toLowerCase();
            const email = (client.email_id || "").toLowerCase();
            const mobile = (client.mobile_number || "").toLowerCase();

            const partnerObj = partners.find((p) => p.uuid === client.user);
            const partnerName = partnerObj
                ? `${partnerObj.first_name || ""} ${partnerObj.last_name || ""}`.toLowerCase()
                : "";

            return (
                fullName.includes(q) ||
                email.includes(q) ||
                mobile.includes(q) ||
                partnerName.includes(q)
            );
        });

        setFilteredClients(filtered);
    }, [search, clients, partners]);

    const handleView = (row: any) => {
        setSelectedUserForView(row);
        setViewDialogOpen(true);
    };

    const handleEdit = (row: any) => {
        setSelectedUserForEdit(row);
        setEditDialogOpen(true);
    };

    const handleDownload = (row: any) => {
        downloadKYCDocs(row, "client", toastr);
    };

    const handleEditUCC = (row: any) => {
        history.push(
            `/partner_client/update_ucc/${row.uuid}?user_type=client`
        );
    };

    const clientDataGridCells = [
        { field: "serial", headerName: "Sr No", width: 120, renderCell: (p: any) => p.api.getRowIndex(p.id) + 1 },
        { field: "inserted_at", headerName: "Created At", width: 170, renderCell: (p: any) => p.value ? new Date(p.value).toLocaleString() : "-" },
        {
            field: "areKYCDetailsVerified", headerName: "KYC Status", width: 160,
        },
        { field: "name", headerName: "Name", width: 150, renderCell: (p: any) => `${p.row.first_name || ""} ${p.row.last_name || ""}` },
        { field: "email_id", headerName: "Email", width: 200 },
        { field: "mobile_number", headerName: "Mobile", width: 130 },
        {
            field: "partner", headerName: "Partner", width: 130,
            valueGetter: (params: any) => {
                const partner = partners.find(p => p.uuid === params.row.user);
                return partner
                    ? partner.company_name ||
                    `${partner.first_name || ""} ${partner.last_name || ""}`.trim()
                    : "";
            }
        },
        { field: "date_of_birth", headerName: "DOB", width: 110, renderCell: (p: any) => p.value || "" },
        { field: "lead_owner", headerName: "Lead Owner", width: 160, renderCell: (p: any) => p.row?.leads_detail?.leadowner || "" },
        { field: "leadsstatus", headerName: "Leads Status", width: 170, renderCell: (p: any) => p.value || "" },
        { field: "isDeleted", headerName: "Deleted", width: 130, renderCell: (p: any) => (p.value ? "true" : "false") },
        { field: "smest_id", headerName: "SMEST ID", width: 150, renderCell: (p: any) => p.value || "" },
        {
            field: "view", headerName: "View", width: 90, sortable: false,
            renderCell: (p: any) => <VisibilityIcon style={{ cursor: "pointer", fontSize: 20 }} onClick={(e) => { e.stopPropagation(); handleView(p.row); }} />,
        },
        {
            field: "edit", headerName: "Edit", width: 90, sortable: false,
            renderCell: (p: any) => <EditIcon style={{ cursor: "pointer", fontSize: 20 }} onClick={(e) => { e.stopPropagation(); handleEdit(p.row); }} />,
        },
        {
            field: "download", headerName: "KYC Docs", width: 130, sortable: false,
            renderCell: (p: any) => <DownloadIcon style={{ cursor: "pointer", fontSize: 20 }} onClick={(e) => { e.stopPropagation(); handleDownload(p.row); }} />,
        },
        {
            field: "edit_ucc", headerName: "Edit UCC", width: 120, sortable: false,
            renderCell: (p: any) => <EditIcon style={{ cursor: "pointer", fontSize: 20, color: "red" }} onClick={(e) => { e.stopPropagation(); handleEditUCC(p.row); }} />,
        },
    ];

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: "95vw",
                        height: "88vh",
                    }
                }}
            >
                <DialogTitle sx={{ position: 'relative', padding: '16px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <SearchIcon />
                        <span>Filter Clients</span>
                    </div>
                    <Button
                        onClick={onClose}
                        sx={{ position: 'absolute', right: 16, top: 16, minWidth: 'auto', padding: '4px' }}
                    >
                        <CloseIcon />
                    </Button>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ height: 550, width: '100%' }}>
                        <DataGrid
                            pagination
                            columns={clientDataGridCells}
                            rows={filteredClients}
                            getRowId={(row) => row.uuid}
                            components={{
                                Toolbar: GridToolbar,
                            }}
                            disableSelectionOnClick
                        />
                    </Box>
                </DialogContent>
            </Dialog>

            <FullScreenDialog
                show={viewDialogOpen}
                heading={`${selectedUserForView?.first_name || ""} ${selectedUserForView?.last_name || ""}`}
                handleClose={() => setViewDialogOpen(false)}
                showSaveButton={true}
                saveButtonText="Save"
                onSumitSaveButton={() => {
                    setViewDialogOpen(false);
                    fetchUsers();
                }}
                inputProps={
                    <PartnerClientDetails
                        data={selectedUserForView}
                        user_type="client"
                    />
                }
            />

            <FullScreenDialog
                show={editDialogOpen}
                heading="Client Update Details"
                handleClose={() => setEditDialogOpen(false)}
                showSaveButton={true}
                saveButtonText="Save"
                onSumitSaveButton={async () => {
                    const success =
                        await editDetailsRef.current?.saveData();

                    if (success) {
                        setEditDialogOpen(false);
                        fetchUsers();
                    }
                }}
                inputProps={
                    <PartnerClientEditDetails
                        ref={editDetailsRef}
                        data={selectedUserForEdit}
                        user_type="client"
                    />
                }
            />

        </>
    );
};